import { useEffect } from 'react';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, OrderStatus } from '@/types/enums';
import { useListenKey, useRefreshListenKey } from '@/lib/api/listen-key';
import { toast } from 'sonner';
import { logConsole } from '@/lib/utils';
import { usePrivyAccount } from '@/hooks';
import { useTransfers } from '@/features/account/api/get-transfers';
import { useFetchAccount } from '@/features/account/api/get-account';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import useFavoritesStore from '@/store/use-favorites-store';
import { parseDecimalToBigInt } from '@/utils/value-format';
import { getChainDecimals } from '@/lib/wagmi/config';
import { defaultChain } from '@/lib/wagmi/config';
import useCreateDeposit from '@/lib/api/create-deposit';
import config from '@/config';
import { useTransfersStore } from '@/store/use-transfers-store';
import { usePrivy } from '@privy-io/react-auth';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { sendEvent } from '@/utils/analytics';
import { EventType } from '@/types';

const MAX_RETRIES = 5;

export default function ApiHandler() {
  const { address, hasAuth } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
    })),
  );

  const authParams = useAccountStore(
    useShallow((state) => state.getAuthParams()),
  );

  const { email, isEmbedded } = usePrivyAccount();
  const { user } = usePrivy();

  useEffect(() => sendEvent(EventType.APP_LAUNCHED, { email }), []);

  const { boot, shutdown } = useIntercom();
  const { i18n } = useTranslation();

  useEffect(() => {
    // https://www.intercom.com/help/en/articles/9423842-enforce-the-language-of-the-messenger-fin-ai-agent-and-workflows-for-specific-users#h_1055838be4
    const languageOverride = (() => {
      switch (i18n.language) {
        case 'en':
          return 'en';
        case 'zh':
          return 'zh-CN';
      }
    })();
    boot({
      actionColor: 'rgb(var(--primary))',
      languageOverride,
      alignment: 'left',
      email,
      name: email ?? address ?? user?.id,
      ...(user ? { createdAt: user.createdAt.getTime(), userId: user.id } : {}),
      customAttributes: {
        address,
        environment: config.isProduction ? 'production' : 'unknown',
      },
    });
    return () => {
      shutdown();
    };
  }, [address, email, user?.id, user?.createdAt, i18n.language]);

  const keyExpiry = authParams?.listenKeyExpiry ?? 0; // in case of null, subtract and comparision works weird.

  const {
    mutateAsync: getListenKey,
    error: _listenKeyError,
    reset: resetRefreshKey,
  } = useListenKey(); // if this error object is not null and is unauthorized, set to false
  const listenKeyError = _listenKeyError as unknown as ErrorCode;

  const {
    mutateAsync: refreshListenKey,
    error: _refreshListenKeyError,
    isPending: isListenKeyPending,
  } = useRefreshListenKey();

  useEffect(() => {
    if (!hasAuth || isListenKeyPending || !address) return;

    if (
      listenKeyError === ErrorCode.UNAUTHORIZED ||
      listenKeyError === ErrorCode.ACCOUNT_NOT_FOUND
    ) {
      resetRefreshKey();
      return;
    }

    const currentTimestamp = useAccountStore.getState().getAccurateTime();
    if (
      !!authParams?.listenKey &&
      authParams.listenKeyExpiry > currentTimestamp
    )
      return;

    let retryCount = 1;
    let interval: number;

    const cleanup = () => window.clearInterval(interval);

    interval = window.setInterval(() => {
      if (retryCount > MAX_RETRIES) {
        cleanup();
        toast.error('Connection failed. Please refresh the page.');
      } else {
        getListenKey(address);
        retryCount++;
      }
    }, 2000); // Retry after 2 seconds

    return () => {
      window.clearInterval(interval);
    }; // Cleanup timeout on unmount or dependency change
  }, [
    address,
    hasAuth,
    authParams?.apiKey,
    isListenKeyPending,
    listenKeyError,
    authParams?.listenKey,
    authParams?.listenKeyExpiry,
  ]);

  useEffect(() => {
    const currentTimestamp = useAccountStore.getState().getAccurateTime();
    let refreshTimeoutId: NodeJS.Timeout;

    if (address && authParams?.listenKey && keyExpiry > currentTimestamp) {
      refreshTimeoutId = setTimeout(() => {
        logConsole(false, 'Refresh Listen Key');
        refreshListenKey(address);
      }, keyExpiry - currentTimestamp);
    }

    return () => {
      clearTimeout(refreshTimeoutId);
    };
  }, [address, authParams?.listenKey, keyExpiry]);

  const {
    data: transferData,
    isPending: areTransfersPending,
    isError: isTransfersError,
  } = useTransfers();

  useFetchAccount();

  const { createDeposit } = useCreateDeposit();

  useEffect(() => {
    if (!config.isDevelopment || !isEmbedded || !address) return;
    if (areTransfersPending || isTransfersError || transferData.length > 0)
      return;
    // check for transfers from WS
    const { transfers: storeTransfers } = useTransfersStore.getState();
    const storeTransfersLength =
      address in storeTransfers
        ? Object.keys(storeTransfers[address]).length
        : 0;
    if (storeTransfersLength > 0) return;
    createDeposit({
      chainId: defaultChain.id,
      size: parseDecimalToBigInt('5000', getChainDecimals(defaultChain.id)),
    });
  }, [
    areTransfersPending,
    address,
    isTransfersError,
    transferData?.length,
    isEmbedded,
  ]);

  useEffect(() => {
    if (!transferData) return;
    transferData.forEach((res) => {
      const transfer = transferResponseToTransfer(res);
      handleTransferForStore(transfer, { skipAccountUpdate: true });
    });
  }, [transferData]);

  useEffect(() => {
    useFavoritesStore.getState().initFavorites();
  }, [address]);

  return <></>;
}
